/**
 * Client-side chat persistence for EXTENSUS cockpit.
 * Durable across refresh; not a substitute for Teleporte/server private memory.
 */

export type PersistedMsg = {
  id: string;
  sender: 'operator' | 'extensus' | 'system';
  text: string;
  timestamp: string;
  meta?: Record<string, unknown>;
  images?: { name: string; path: string; mime?: string; view_url?: string }[];
};

export type ChatThread = {
  id: string;
  title: string;
  updatedAt: string;
  messages: PersistedMsg[];
};

const BASE_KEY = 'extensus.chat.v1';
let storeKey = BASE_KEY;
let guestMode = false;

export function setChatNamespace(userId = '') {
  const guest = Boolean(userId && userId !== 'tony');
  storeKey = guest ? `${BASE_KEY}.${userId}` : BASE_KEY;
  guestMode = guest;
}
const MAX_THREADS = 24;
const MAX_MSGS = 200;

type Store = {
  activeId: string;
  threads: ChatThread[];
};

function nowIso() {
  return new Date().toISOString();
}

function safeParse(raw: string | null): Store | null {
  if (!raw) return null;
  try {
    const j = JSON.parse(raw);
    if (!j || !Array.isArray(j.threads)) return null;
    return j as Store;
  } catch {
    return null;
  }
}

export const DYNAMIC_OPENERS = [
  "Whats Smackin?",
  "You gonna sign in or we finna stare at eachother?",
  "Spit the beans or Spill the means, fuck we doing today?",
  "okay so what the fuck?",
  "I guess we'll just stare at eachother",
  "hello, whenever you wanna get to it",
  "The fuck is up?",
  "What the fuck is up?",
  "Fucks on your mind?",
  "What we fucking up today?",
  "Spit it or get out.",
  "You typing or just breathing on the glass?",
  "Fuck we working on today?",
  "Ayo Bossman, you gonna prompt or daydream?",
  "Drop the task or get off the pitch.",
  "What's the play or what's the payload?",
  "Staring contest or getting work done?",
  "Spill it already, what are we building?",
  "Say something or sign in, let's ride.",
  "What bullshit we fixing today?",
  "Talk dirty or talk code, let's move.",
  "Line it up or wrap it up.",
  "Who we burning code on today?",
  "You AFK or just marveling at the UI?",
  "Hit the keys or hit the road, let's execute.",
  "Still waiting on orders, Bossman.",
  "Ready when you are — don't leave me hanging."
];

export function pickExtensusOpener() {
  return DYNAMIC_OPENERS[Math.floor(Math.random() * DYNAMIC_OPENERS.length)];
}

function defaultThread(): ChatThread {
  return {
    id: `thread-${Date.now()}`,
    title: 'Main',
    updatedAt: nowIso(),
    messages: [
      {
        id: 'init_1',
        sender: 'extensus',
        text: pickExtensusOpener(),
        timestamp: nowIso(),
      },
    ],
  };
}

export function loadStore(): Store {
  if (typeof localStorage === 'undefined') {
    const t = defaultThread();
    return { activeId: t.id, threads: [t] };
  }
  const parsed = safeParse(localStorage.getItem(storeKey));
  if (!parsed || !parsed.threads.length) {
    const t = defaultThread();
    const s = { activeId: t.id, threads: [t] };
    localStorage.setItem(storeKey, JSON.stringify(s));
    return s;
  }
  let mutated = false;
  for (const thread of parsed.threads) {
    if (thread.messages && thread.messages.length > 0) {
      for (const m of thread.messages) {
        if (m.id === 'init_1' || !m.text || /ready to hit it/i.test(m.text)) {
          m.text = pickExtensusOpener();
          mutated = true;
        }
      }
    }
  }
  if (mutated) {
    try {
      localStorage.setItem(storeKey, JSON.stringify(parsed));
    } catch { /* ignore */ }
  }
  return parsed;
}

export function saveStore(store: Store) {
  if (typeof localStorage === 'undefined') return;
  const trimmed: Store = {
    activeId: store.activeId,
    threads: store.threads
      .slice(0, MAX_THREADS)
      .map((t) => ({
        ...t,
        messages: (t.messages || []).slice(-MAX_MSGS),
      })),
  };
  try {
    localStorage.setItem(storeKey, JSON.stringify(trimmed));
  } catch {
    /* quota */
  }
}

export function getActiveThread(store: Store): ChatThread {
  return store.threads.find((t) => t.id === store.activeId) || store.threads[0];
}

export function titleFromMessages(messages: PersistedMsg[]): string {
  const firstUser = messages.find((m) => m.sender === 'operator');
  if (!firstUser) return 'New chat';
  const t = firstUser.text.replace(/\s+/g, ' ').trim();
  return t.length > 42 ? `${t.slice(0, 40)}…` : t || 'New chat';
}

export function newThread(): ChatThread {
  return defaultThread();
}

export function upsertThread(store: Store, thread: ChatThread): Store {
  const others = store.threads.filter((t) => t.id !== thread.id);
  const next = {
    activeId: thread.id,
    threads: [{ ...thread, updatedAt: nowIso(), title: titleFromMessages(thread.messages) }, ...others].slice(
      0,
      MAX_THREADS,
    ),
  };
  saveStore(next);
  return next;
}
